// @flow

import * as React from 'react';
import type { ChildrenArray, Element } from 'react';
import { Component } from 'react';
import styled from 'styled-components';
import { Item } from './Item';
import Downshift from 'downshift';
import _ from 'lodash';
import MagnifierImage from '../../../assets/magnifier.svg';

type AutocompleteProps = {
  id: string,
  onChange: (newValue: string) => void,
  onSelect?: (newValue: string) => void,
  disabled?: boolean,
  pageSize: number,
  placeholder?: string,
  selectedValue: string,
  children: ChildrenArray<Element<typeof Item>>, // array of Item
  error?: boolean,
  inputValue?: string,
  onInputValueChange?: (input: string, stateAndObjectHelpers: Object) => void,
  label?: any,
  filterCondition?: (item: any, inputValue: string) => boolean,
};

type ItemObject = {
  value: string,
  displayText: string,
  component: ChildrenArray<Element<typeof Item>>,
};

type State = {
  input: string,
  itemObjectList: Array<ItemObject>,
};

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const MenuContainer = styled.div`
  border: solid 1px #dbe0de;
  border-top: 0;
  border-radius: 2px;
  position: absolute;
  width: 100%;
  z-index: 1;
  &:nth-child(even) {
    background-color: ${(props) => (props.isFocused ? '#bfcdd4' : '#f9fafa')};
  }
`;

const AutocompleteContainer = styled.div`
  height: 46px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
`;

const StyledItem = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  flex: 1;
  flex-direction: row;
  padding: 12px 18px;
  min-height: 48px;
  font-weight: ${(props) => (props.isSelected ? 'bold' : '')};
  &:nth-child(odd) {
    background-color: ${(props) => (props.isActive ? '#bfcdd4' : 'white')};
  }
  &:nth-child(even) {
    background-color: ${(props) => (props.isActive ? '#bfcdd4' : '#f9fafa')};
  }
`;

const TextInputField = styled.input`
  height: 46px;
  width: 100%;
  border-radius: 2px;
  background:
    url(${MagnifierImage}) no-repeat 95% center,
    linear-gradient(to bottom, #ffffff, #fafafa);
  background-position: calc(100% - 15px) center;
  display: flex;
  border: solid 1px #dbe0de;
  flex-direction: row;
  padding: 0 36px 0 18px;

  ${(props) =>
    props['aria-invalid'] === 'true'
      ? props.theme.errorColor
      : props.theme.borderColor};
  font-size: 1rem;
  color: ${(props) => props.theme.textColor};

  &:focus {
    border: 1px solid ${(props) => props.theme.actionColor};
    outline: 0;
  }

  &::-webkit-input-placeholder {
    color: #7f9baa;
  }
  &::-moz-placeholder {
    opacity: 1;
    color: #7f9baa;
  }
  &:-ms-input-placeholder {
    color: #7f9baa;
  }
  border-color: ${(props) => (props.error ? props.theme.errorColor : '')};
  border-width: ${(props) => (props.error ? '2px' : '')};
`;

const StyledLabel = styled.label`
  font-size: 0.875rem;
  line-height: 1.29;
  color: ${(props) => props.theme.textColor};
  display: block;
  margin-bottom: 0.5rem;
`;

export default class AutoComplete extends Component<AutocompleteProps, State> {
  static Item = Item;

  static defaultProps = {
    selectedValue: '',
    pageSize: 5,
    onChange: (item: any) => {},
  };

  state = {
    value: '',
    input: '',
    itemObjectList: [],
  };

  itemToString = (item: any) => {
    return item ? item.displayText : '';
  };

  handleInput = (inputValue: string, stateAndHelpers: Object) => {
    this.setState({ input: inputValue });
  };

  filterCondition = (item: any, inputValue: string) => {
    return _.startsWith(
      item.props.displayText.toLowerCase(),
      inputValue.toLowerCase()
    );
  };

  UNSAFE_componentWillReceiveProps(
    nextProps: AutocompleteProps,
    nextState: State
  ) {
    this.setState({
      itemObjectList: React.Children.map(this.props.children, (item) => {
        return ({
          value: item.props.value,
          displayText: item.props.displayText,
          component: item,
        }: ItemObject);
      }),
    });
  }

  renderAutocomplete(
    items: ChildrenArray<Element<typeof Item>>,
    selItem: void | ItemObject
  ) {
    return (
      <Downshift
        onSelect={(item) => {
          if (this.props.onSelect) {
            this.props.onSelect(item);
            const selectedChild = _.find(
              React.Children.toArray(items),
              (i) => i.props.value === item
            );

            if (selectedChild)
              this.setState({
                input: selectedChild.props && selectedChild.props.displayText,
              });
          }
        }}
        onChange={(item) => {
          this.props.onChange(item);
          if (!this.props.onSelect) {
            const selectedChild = _.find(
              React.Children.toArray(items),
              (i) => i.props.value === item
            );
            if (selectedChild)
              this.setState({
                input: selectedChild.props && selectedChild.props.displayText,
              });
          }
        }}
        selectedItem={selItem}
        itemToString={this.itemToString}
        onInputValueChange={this.props.handleInput || this.handleInput}
        inputValue={
          this.props.handleInput ? this.props.inputValue : this.state.input
        }
      >
        {({
          getRootProps,
          getItemProps,
          getLabelProps,
          getInputProps,
          isOpen,
          highlightedIndex,
          clearSelection,
          itemToString,
          selectedItem,
          inputValue,
        }) => {
          const filterCondition =
            this.props.filterCondition || this.filterCondition;

          return (
            <Container {...getRootProps({ refKey: 'ref' })}>
              <StyledLabel {...getLabelProps()}>{this.props.label}</StyledLabel>
              <AutocompleteContainer id={this.props.id}>
                <TextInputField
                  error={this.props.error}
                  {...getInputProps({
                    autoComplete: 'turn-off',
                    // default value for downshift is 'off',
                    // but it is ignored in chrome, see:
                    // https://bugs.chromium.org/p/chromium/issues/detail?id=914451&can=2&q=Autofill%20&colspec=ID%20Pri%20M%20Stars%20ReleaseBlock%20Component%20Status%20Owner%20Summary%20OS%20Modified#c66
                    isOpen,
                    placeholder: this.props.placeholder,
                  })}
                />
              </AutocompleteContainer>
              {isOpen ? (
                <MenuContainer>
                  {inputValue &&
                    React.Children.toArray(items)
                      .filter(
                        (i) => inputValue && filterCondition(i, inputValue)
                      )
                      .map((item, index) => {
                        const { id, value } = item.props;
                        return (
                          <StyledItem
                            id={
                              id ||
                              (this.props.id ? this.props.id.concat(value) : '')
                            }
                            {...getItemProps({
                              key: value,
                              item: value,
                              index: index,
                              isActive: highlightedIndex === index,
                              isSelected:
                                selectedItem && selectedItem.value === value,
                            })}
                          >
                            {item}
                          </StyledItem>
                        );
                      })
                      .slice(0, this.props.pageSize)}
                </MenuContainer>
              ) : null}
            </Container>
          );
        }}
      </Downshift>
    );
  }

  render() {
    const selectedItem: void | ItemObject = this.state.itemObjectList.find(
      (item) => {
        return item.value === this.props.selectedValue;
      }
    );
    return this.renderAutocomplete(this.props.children, selectedItem);
  }
}
